import {
	defaultColDefination,
	getColumnDefFragmentation,
	initialGridData,
} from 'CategoryManagement/Explore/DetailedAnalysis/utils';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
} from 'react';
import { getFragmentedCategories } from 'utils/apiRequests';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';
import {
	getHeaderByTaxonomyType,
	getRequiredCustomFilterObjectFormat,
} from '../../CategoryManagement/utils';

const FragmentedCategoriesContext = createContext();

const categoriesText = 'categories';

export const FragmentedCategoriesProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [analysis, setAnalysis] = useState(`0 ${categoriesText}`);
	const [gridData, setGridData] = useState(initialGridData);
	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching, isSaving, vendorCountMax } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	const taxonomyHeader = useMemo(() => getHeaderByTaxonomyType(taxonomyType), [
		taxonomyType,
	]);

	useEffect(
		() => {
			if (!isSaving && !isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getFragmentedCategories(payload)
						.then(response => {
							if (response.succeeded) {
								if (response.data === null) {
									setAnalysis(`No ${categoriesText}.`);
									setIsLoading(false);
									setGridData(initialGridData);
								} else {
									if (response.data.length > 0 || response.data !== null) {
										if (!!Object.keys(response.data).length) {
											const analysisString = `${formatNumber(
												response.data.fragmentedCategories
											)} ${categoriesText}`;
											setAnalysis(analysisString);
											setIsLoading(false);
										}
									}
									const categoryLevel = categoryName.split('/').length - 2;

									if (response.data.explore.length > 0) {
										const filteredData = response.data.explore
											.filter(el => el.vendorCount >= vendorCountMax)
											.map(x => {
												return {
													...x,
													totalSpend: formatSpendWithDecimalAndSymbol(
														x?.totalSpend,
														currencySymbol,
														hideDecimals,
														2
													).toUpperCase(),
												};
											});
										const colResult = getColumnDefFragmentation(
											categoryLevel,
											taxonomyHeader
										);
										setGridData(prevState => ({
											...prevState,
											rowData: filteredData,
											columnDefs: colResult,
											defaultColDef: defaultColDefination,
										}));
									}
								}
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			isSaving,
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<FragmentedCategoriesContext.Provider
			value={{ isLoading, analysis, gridData }}
		>
			{children}
		</FragmentedCategoriesContext.Provider>
	);
};

export const useFragmentedCategoriesContext = () => {
	return useContext(FragmentedCategoriesContext);
};
